import { Trans } from '@lingui/react/macro';
import { useCallback } from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
} from '@chakra-ui/react';
import { IdCard, Mail, CalendarClock } from 'lucide-react';
import {
  FormContentContainer,
  FormFooterContainer,
  FormMainContainer,
} from '@duke-hq/ui/components/Modals/FormModalLayouts';

type Enrolment = {
  id: number;
  course: {
    id: string;
    identifier: string;
    title: string;
    coachingCallLink: string | null;
  };
};

type Student = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  picture_url?: string;
  universal_student_identifier?: string;
};

function BookCoachingCall({ student, enrolment }: { student: Student; enrolment: Enrolment }) {
  const hasCoachingCallLink = Boolean(enrolment.course?.coachingCallLink);

  const openScheduleCallLink = useCallback(() => {
    if (hasCoachingCallLink && enrolment.course.coachingCallLink) {
      window.open(enrolment.course.coachingCallLink, '_blank');
    }
  }, [enrolment, hasCoachingCallLink]);

  return (
    <FormMainContainer>
      <FormContentContainer>
        <Box
          w="full"
          borderRadius="6px"
          bg="bg.muted"
          border="1px solid"
          borderColor="border.emphasis"
          padding="8px 12px"
          gap="12px"
        >
          <HStack align="center" spacing="4" mb={2}>
            <Text fontWeight="medium">
              <Trans>Booking for</Trans> {student.firstName} {student.lastName}
            </Text>
            {student.universal_student_identifier && (
              <HStack align="center" spacing="1" gap={2}>
                <Icon as={IdCard} boxSize="16px" />
                <Text fontSize="sm" lineHeight="1.25" color="fg.muted">
                  {student.universal_student_identifier}
                </Text>
              </HStack>
            )}
            <HStack align="center" spacing="1" gap={2}>
              <Icon as={Mail} boxSize="16px" />
              <Text fontSize="sm" lineHeight="1.25" color="fg.muted">
                {student.email}
              </Text>
            </HStack>
          </HStack>

          <Box
            p="8px 12px"
            my="6px"
            border="1px solid"
            borderRadius="8px"
            borderColor="white"
            bg="bg.canvas"
          >
            <HStack align="center" spacing="4" justifyContent="space-between" w="full">
              <HStack align="center">
                <Text color="fg.muted">{enrolment.course.identifier}</Text>
                <Text fontWeight="medium">{enrolment.course.title}</Text>
              </HStack>
            </HStack>
          </Box>
        </Box>

        {!hasCoachingCallLink && (
          <Alert
            status="warning"
            borderRadius="8px"
            border="1px solid"
            borderColor="warning"
            justifyContent="space-between"
            alignItems="start"
            mt={2}
          >
            <Flex alignItems="start" gap={1}>
              <AlertIcon />
              <Box>
                <AlertTitle fontSize="sm">
                  <Trans>No coaching calls available</Trans>
                </AlertTitle>
                <AlertDescription>
                  <Text fontSize="sm">
                    <Trans>Unfortunately, coaching calls are not available for</Trans>{' '}
                    <strong>{enrolment.course.identifier}</strong> <Trans>at this time.</Trans>
                  </Text>
                </AlertDescription>
              </Box>
            </Flex>
          </Alert>
        )}
      </FormContentContainer>

      <FormFooterContainer>
        <Button
          onClick={openScheduleCallLink}
          isDisabled={!hasCoachingCallLink}
          leftIcon={<CalendarClock />}
        >
          <Trans>Schedule a call</Trans>
        </Button>
      </FormFooterContainer>
    </FormMainContainer>
  );
}

function EnrolmentBookCoachingModal({
  enrolment,
  student,
  isOpen,
  onClose,
}: {
  enrolment: Enrolment;
  student: Student;
  isOpen: boolean;
  onClose: () => void;
}): React.ReactElement {
  return (
    <Modal onClose={onClose} isOpen={isOpen} isCentered closeOnOverlayClick={false} size="3xl">
      <ModalOverlay />
      <ModalContent h="500px" bg="bg.surface">
        <ModalHeader my="10px">
          <Trans>Book a coaching call</Trans>
        </ModalHeader>
        <ModalCloseButton size="xs" />
        <ModalBody h="full" p="0px" m="0px">
          {student && enrolment ? (
            <BookCoachingCall student={student} enrolment={enrolment} />
          ) : null}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

export default EnrolmentBookCoachingModal;
